import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import type { QuoteType } from '@engine/models/admin'

import { AdminFilters } from '#components/adminFilters/AdminFiltersPanel.js'
import { FilterInput } from '#components/adminFilters/FilterInput.js'
import { FilterRow } from '#components/adminFilters/FilterRow.js'
import { FilterSelect } from '#components/adminFilters/FilterSelect.js'
import { ScacTypeahead } from '#components/ScacTypeahead.js'
import { useDebounce } from '#hooks/useDebounce'
import {
  getRangeOptions,
  RANGE_MINUTES,
  type RangeKey,
} from '#utils/timeRanges'

interface LocalFiltersState {
  carrier: string
  range: RangeKey
  customFrom: string
  customTo: string
  quoteType: QuoteType
}

export const useQuotesFilters = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  const [localFilters, setLocalFilters] = useState<LocalFiltersState>({
    carrier: searchParams.get('carrier') || '',
    range: (searchParams.get('range') as RangeKey) || '30d', // Default to 30 days
    customFrom: searchParams.get('customFrom') || '',
    customTo: searchParams.get('customTo') || '',
    quoteType: (searchParams.get('quoteType') as QuoteType) || 'all',
  })

  const debouncedLocalFilters = useDebounce(localFilters, 500)

  const filters = useMemo(() => {
    const now = new Date()
    let startDate: string | undefined
    let endDate: string | undefined

    if (debouncedLocalFilters.range === 'all') {
      startDate = undefined
      endDate = undefined
    } else if (debouncedLocalFilters.range === 'custom') {
      startDate = debouncedLocalFilters.customFrom
        ? new Date(debouncedLocalFilters.customFrom).toISOString()
        : undefined
      endDate = debouncedLocalFilters.customTo
        ? new Date(debouncedLocalFilters.customTo).toISOString()
        : undefined
    } else {
      const minutes = RANGE_MINUTES[debouncedLocalFilters.range]
      // For charts, we probably want to align to start of day if possible,
      // but the API handles date parsing.
      // Let's just subtract the time.
      const fromDate = new Date(now.getTime() - minutes * 60 * 1_000)
      startDate = fromDate.toISOString()
      endDate = now.toISOString()
    }

    return {
      carrier: debouncedLocalFilters.carrier || undefined,
      startDate,
      endDate,
      quoteType: debouncedLocalFilters.quoteType,
    }
  }, [debouncedLocalFilters])

  useEffect(() => {
    const params = new URLSearchParams()
    if (debouncedLocalFilters.carrier)
      params.set('carrier', debouncedLocalFilters.carrier)
    if (debouncedLocalFilters.range)
      params.set('range', debouncedLocalFilters.range)
    if (debouncedLocalFilters.customFrom)
      params.set('customFrom', debouncedLocalFilters.customFrom)
    if (debouncedLocalFilters.customTo)
      params.set('customTo', debouncedLocalFilters.customTo)
    if (debouncedLocalFilters.quoteType)
      params.set('quoteType', debouncedLocalFilters.quoteType)
    setSearchParams(params, { replace: true })
  }, [debouncedLocalFilters, setSearchParams])

  return { filters, localFilters, setLocalFilters }
}

interface QuotesFiltersProps {
  isLoading: boolean
  onRefresh: () => void
  localFilters: LocalFiltersState
  setLocalFilters: (filters: LocalFiltersState) => void
}

export const QuotesFilters = ({
  isLoading,
  onRefresh,
  localFilters,
  setLocalFilters,
}: QuotesFiltersProps) => {
  return (
    <AdminFilters isLoading={isLoading} onRefresh={onRefresh}>
      <FilterRow>
        <FilterSelect
          label="Quote Type"
          value={localFilters.quoteType}
          onChange={value =>
            setLocalFilters({
              ...localFilters,
              quoteType: value as QuoteType,
            })
          }
          options={[
            { value: 'all', label: 'All' },
            { value: 'volume', label: 'Volume' },
            { value: 'dynamic', label: 'Dynamic' },
          ]}
        />
        <ScacTypeahead
          value={localFilters.carrier}
          onChange={value =>
            setLocalFilters({ ...localFilters, carrier: value })
          }
        />
        <FilterSelect
          label="Time range"
          value={localFilters.range}
          onChange={value =>
            setLocalFilters({ ...localFilters, range: value as RangeKey })
          }
          options={getRangeOptions()}
        />
        {localFilters.range === 'custom' ? (
          <>
            <FilterInput
              label="From"
              value={localFilters.customFrom}
              onChange={value =>
                setLocalFilters({ ...localFilters, customFrom: value })
              }
              type="datetime-local"
            />
            <FilterInput
              label="To"
              value={localFilters.customTo}
              onChange={value =>
                setLocalFilters({ ...localFilters, customTo: value })
              }
              type="datetime-local"
            />
          </>
        ) : null}
      </FilterRow>
    </AdminFilters>
  )
}
